import React, { useContext, useMemo } from 'react';
import { Spin } from 'antd';
import { EchartsWithTheme, Empty, Modal, useTheme } from '@maxtropy/components';
import {
  apiV2GasMonitorMapStationAreaDataPost,
  V2GasMonitorMapStationAreaDataPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import { useRequest } from 'ahooks';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash-es';
import { ShareDataWrapperContext } from '@/pages/WgqAllMaps';
import { getEnergyMediumColor, getEnergyMediumColorMap } from '@/pages/WgqAllMaps/utils';
import { formatThousandAndUnit, formatThousandUnit, keepOneOrLine, keepOnePercentOrLine } from '@/utils/utils';
import titleIcon from '@/assets/images/title_icon.png';
import circleBig from '../../../imgs/circle-big@2x.png';
import outerStyles from '../index.module.scss';
import styles from './index.module.scss';

const PIE_OUTER_SIZE = 180;
const PIE_CENTER_IMAGE_SIZE = 108;
const PIE_CENTER_X = 104;
const PIE_CENTER_Y = 104;

interface AreaGasOverviewModalProps {
  open: boolean;
  areaName?: string;
  zoneName?: string;
  onClose: () => void;
}

type AreaData = NonNullable<V2GasMonitorMapStationAreaDataPostResponse['areaData']>;

const AreaGasOverviewModal: React.FC<AreaGasOverviewModalProps> = ({ open, areaName, zoneName, onClose }) => {
  const theme = useTheme();
  const { isFullScreen, scale } = useContext(ShareDataWrapperContext);

  const { data, loading } = useRequest(() => apiV2GasMonitorMapStationAreaDataPost({ zoneName }), {
    ready: open && !!zoneName,
    refreshDeps: [open, zoneName],
  });

  const areaData: AreaData | undefined = data?.areaData;
  const resilienceList = useMemo(() => areaData?.resilienceList ?? [], [areaData]);
  const gasUsageList = useMemo(() => areaData?.gasUsageList ?? [], [areaData]);
  const trendList = useMemo(() => areaData?.trendList ?? [], [areaData]);
  const totalUsage = useMemo(() => gasUsageList.reduce((sum, item) => sum + (item.usage ?? 0), 0), [gasUsageList]);
  const displayUnit = useMemo(() => {
    return data?.unit ?? gasUsageList.find(item => item.physicalUnitName)?.physicalUnitName ?? '-';
  }, [data?.unit, gasUsageList]);

  const mediumColorMap = useMemo(() => {
    return getEnergyMediumColorMap([...gasUsageList, ...trendList]);
  }, [gasUsageList, trendList]);

  const pieOption = useMemo(() => {
    const chartData = gasUsageList.map((item, index) => ({
      name: item.energyMediumName ?? '--',
      value: item.usage ?? 0,
      proportion: item.proportion ?? 0,
      color: getEnergyMediumColor({
        energyMediumId: item.energyMediumId,
        energyMediumName: item.energyMediumName,
        colorMap: mediumColorMap,
        fallbackIndex: index,
      }),
      unit: '元',
    }));
    const total = formatThousandAndUnit(totalUsage, 1);

    return {
      backgroundColor: 'transparent',
      color: chartData.map(d => d.color),
      tooltip: {
        confine: true,
        formatter: (params: any) => `
          <div style="min-width:140px;">
            <div style="display:flex;gap:8px;align-items:center;">
              <span>${params.marker}</span>
              <span>${params.name}</span>
              <span style="font-weight:600;">${params.value}%</span>
            </div>
          </div>
        `,
      },
      title: {
        text: `${areaName || '--'}(元)`,
        textStyle: {
          fontSize: 14,
          color: 'rgba(255,255,255,0.85)',
          fontWeight: 400,
        },
        subtext: `{value|${total.value}${total.unit}}`,
        subtextStyle: {
          rich: {
            value: {
              fontSize: 20,
              color: '#fff',
              fontWeight: 500,
              textShadowBlur: 6,
              textShadowColor: 'rgba(82, 231, 255, 0.45)',
            },
          },
        },
        left: PIE_CENTER_X,
        top: PIE_CENTER_Y - 24,
        itemGap: 4,
        textAlign: 'center',
      },
      legend: {
        type: 'scroll',
        show: true,
        orient: 'vertical',
        top: 22,
        left: PIE_OUTER_SIZE + 40,
        height: 122,
        itemWidth: 7,
        itemHeight: 7,
        itemGap: 7,
        icon: 'circle',
        pageButtonItemGap: 4,
        pageButtonGap: 8,
        pageButtonPosition: 'end',
        pageIconColor: '#52e7ff',
        pageIconInactiveColor: 'rgba(255, 255, 255, 0.25)',
        pageTextStyle: {
          color: 'rgba(255, 255, 255, 0.65)',
          fontSize: 12,
        },
        formatter: (name: string) => {
          const current = chartData.find(item => item.name === name);
          if (!current) return name;
          const usage = formatThousandAndUnit(current.value, 1);
          return `{name|${name}}{percent|${keepOnePercentOrLine(current.proportion)}%}{amount|${usage.value}${
            usage.unit
          }${current.unit}}`;
        },
        textStyle: {
          color: 'rgba(255, 255, 255, 0.65)',
          fontSize: 13,
          lineHeight: 18,
          rich: {
            name: {
              width: 82,
              color: 'rgba(255, 255, 255, 0.65)',
              fontSize: 13,
            },
            percent: {
              width: 54,
              align: 'right',
              color: 'rgba(255, 255, 255, 0.85)',
              fontSize: 13,
              fontWeight: 500,
            },
            amount: {
              width: 94,
              align: 'right',
              color: 'rgba(255, 255, 255, 0.65)',
              fontSize: 12,
            },
          },
        },
        data: chartData.map(item => item.name),
      },
      graphic: {
        elements: [
          {
            type: 'image',
            x: PIE_CENTER_X - PIE_CENTER_IMAGE_SIZE / 2,
            y: PIE_CENTER_Y - PIE_CENTER_IMAGE_SIZE / 2,
            bounding: 'raw',
            z: 1,
            silent: true,
            style: {
              image: circleBig,
              width: PIE_CENTER_IMAGE_SIZE,
              height: PIE_CENTER_IMAGE_SIZE,
            },
            origin: [PIE_CENTER_IMAGE_SIZE / 2, PIE_CENTER_IMAGE_SIZE / 2],
            keyframeAnimation: [
              {
                duration: 40000,
                loop: true,
                easing: 'linear',
                keyframes: [
                  { percent: 0, rotation: 0, transition: ['rotation'] },
                  { percent: 1, rotation: Math.PI * 2, transition: ['rotation'] },
                ],
              },
            ],
          },
        ],
      },
      series: [
        {
          type: 'pie',
          radius: [58, PIE_OUTER_SIZE / 2],
          center: [PIE_CENTER_X, PIE_CENTER_Y],
          padAngle: 2,
          itemStyle: {
            borderRadius: 6,
          },
          label: { show: false },
          emphasis: {
            label: { show: false },
            scaleSize: 8,
          },
          data: chartData.map(d => ({ name: d.name, value: d.value })),
        },
      ],
    };
  }, [areaName, displayUnit, gasUsageList, mediumColorMap, totalUsage]);

  const trendOption = useMemo(() => {
    const timeList = Array.from(
      new Set(
        trendList

          .flatMap(item => item?.hourlyData ?? [])
          .map(item => item.ts)
          .filter((ts): ts is number => ts !== undefined)
      )
    ).sort((a, b) => a - b);
    const seriesData = trendList.map(item => ({
      name: item?.energyMediumName ?? '--',
      color: getEnergyMediumColor({
        energyMediumId: item?.energyMediumId,
        energyMediumName: item?.energyMediumName,
        colorMap: mediumColorMap,
      }),

      hourlyData: item?.hourlyData ?? [],
    }));

    return {
      backgroundColor: 'transparent',
      color: seriesData.map(item => item.color),
      grid: {
        left: 24,
        right: 20,
        top: 38,
        bottom: 8,
        containLabel: true,
      },
      legend: {
        top: 0,
        right: 4,
        icon: 'rect',
        itemWidth: 12,
        itemHeight: 12,
        itemGap: 14,
        data: seriesData.map(item => item.name),
      },
      tooltip: {
        trigger: 'axis',
        confine: true,
        formatter: (params: any[]) => {
          if (!params?.length) return '';
          return `
            <div style="width:220px;box-sizing:border-box;">
              <div style="margin-bottom:8px;">${params[0].axisValue}</div>
              ${params
                .map(
                  item => `
                    <div style="display:flex;align-items:center;justify-content:space-between;gap:12px;line-height:22px;">
                      <span>${item.marker}${item.seriesName}</span>
                      <span style="text-align:right;">${
                        item.value === null || item.value === undefined ? '--' : formatThousandUnit(item.value, 1)
                      }${displayUnit}</span>
                    </div>
                  `
                )
                .join('')}
            </div>`;
        },
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: timeList.map(item => dayjs(item).format('HH:mm')),
      },
      yAxis: {
        type: 'value',
        name: displayUnit,
        nameTextStyle: {
          padding: [0, 24, 0, 0],
        },
      },
      series: seriesData.map(item => {
        const dataMap = new Map(item.hourlyData.map(point => [point.ts, point.usage]));
        return {
          name: item.name,
          type: 'line',
          smooth: true,
          showSymbol: false,
          lineStyle: {
            width: 1.5,
            color: item.color,
          },
          itemStyle: {
            color: item.color,
          },
          data: timeList.map(ts => dataMap.get(ts) ?? null),
        };
      }),
    };
  }, [displayUnit, mediumColorMap, trendList]);

  const hasContent = !isEmpty(resilienceList) || !isEmpty(gasUsageList) || !isEmpty(trendList);

  return (
    <Modal
      title={areaName || '区域用气概览'}
      open={open}
      onCancel={onClose}
      footer={null}
      width={1200}
      mask={false}
      destroyOnClose
      centered
      style={{ top: isFullScreen ? 80 * (scale ?? 1) : 110 * (scale ?? 1) }}
      className={`${outerStyles.customModal} ${styles.areaModal}`}
      getContainer={() => document.getElementById('full_screen_box') ?? document.body}
    >
      <Spin spinning={loading}>
        {!hasContent ? (
          <Empty style={{ height: 420, paddingTop: 150 }} />
        ) : (
          <div className={styles.modalContent}>
            <div className={styles.topGrid}>
              <section className={styles.section}>
                <div className={styles.sectionTitle}>
                  <img src={titleIcon} alt="" className={styles.titleIcon} />
                  <span>区域实时供气韧性</span>
                </div>
                {isEmpty(resilienceList) ? (
                  <Empty className={styles.resilienceEmpty} />
                ) : (
                  <div className={styles.resilienceGrid}>
                    {resilienceList.map((item, index) => {
                      const percent = (item.proportion ?? 0) * 100;
                      return (
                        <div key={`${item.energyMediumName ?? index}`} className={styles.resilienceItem}>
                          <span className={styles.resilienceName}>{item.energyMediumName ?? '--'}</span>
                          <div className={styles.resilienceMetrics}>
                            <div className={styles.progressTrack}>
                              <span className={styles.progressBar} style={{ width: `${Math.min(percent, 100)}%` }} />
                            </div>
                            <span className={styles.resilienceValue}>{keepOneOrLine(item.proportion)}%</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
              <section className={styles.section}>
                <div className={styles.sectionTitle}>
                  <img src={titleIcon} alt="" className={styles.titleIcon} />
                  <span>今日气体成本占比</span>
                </div>
                <EchartsWithTheme
                  option={pieOption}
                  theme={theme}
                  style={{ width: '100%', height: 210, paddingLeft: 24, marginTop: 24 }}
                />
              </section>
            </div>
            <section className={styles.section}>
              <div className={styles.sectionTitle}>
                <img src={titleIcon} alt="" className={styles.titleIcon} />
                <span>用气趋势</span>
              </div>
              <EchartsWithTheme option={trendOption} theme={theme} style={{ width: '100%', height: 210 }} />
            </section>
          </div>
        )}
      </Spin>
    </Modal>
  );
};

export default AreaGasOverviewModal;
